"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import CdnImage from "@/components/CdnImage";
import { useKalshiLiveState } from "@/hooks/useKalshiLiveState";
import { useKalshiLiveTickers } from "@/hooks/useKalshiLiveTickers";
import type { Market, Source, OpenTradingModalFn, GetLivePriceFn, GetLiveBuyPricesFn, FormatVolumeFn } from "./types";
import type { EventData } from "@/hooks/useEventQuery";
import { useT } from "@/hooks/useT";

// Same shape as the map EventMarketsGrid + EventCard consume. Polymarket
// markets in Serie A / La Liga / etc. all share the league logo as
// `market.image` (e.g. Serie-A-Logo.png), so we have to look up the real
// per-team logo from our sports-live aggregation. Cached app-wide via
// React Query so this and EventMarketsGrid share one payload.
function useTeamLogos() {
  return useQuery<Record<string, string>>({
    queryKey: ["sports-live", "team-logos"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/sports-live/team-logos");
        if (!res.ok) return {};
        const data = await res.json();
        return (data?.logos || {}) as Record<string, string>;
      } catch {
        return {};
      }
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });
}

function lookupTeamLogo(logos: Record<string, string>, teamName: string | null | undefined): string | null {
  if (!teamName || !logos) return null;
  const key = teamName.trim().toLowerCase();
  if (!key) return null;
  if (logos[key]) return logos[key];
  const suffix = `:${key}`;
  for (const k of Object.keys(logos)) {
    if (k.endsWith(suffix)) return logos[k];
  }
  return null;
}

interface SportsMatchViewProps {
  markets: Market[];
  eventData: EventData;
  source: Source;
  slug: string;
  widgetConfig: any;
  getLivePrice: GetLivePriceFn;
  getLiveBuyPrices: GetLiveBuyPricesFn;
  openTradingModal: OpenTradingModalFn;
  formatVolume: FormatVolumeFn;
}

function getTeamName(market: Market): string {
  // Kalshi: market.title is the player/team name directly (no "Will X win" wrapper)
  const q = (market as any).title || market.question || '';
  const match = q.match(/^Will\s+(.+?)\s+win/i);
  let name = match ? match[1] : q.replace(/^Will\s+/i, '').replace(/\s+win.*$/i, '');
  // Strip Polymarket's trailing season-record block ("Sevilla FC (9-7-17)" →
  // "Sevilla FC"). Keeps the team label clean — record can be surfaced as
  // separate metadata if we ever want it.
  return name.replace(/\s*\(\d+(?:-\d+){1,2}\)\s*$/, '').trim();
}

const LEAGUE_NAMES: Record<string, string> = {
  'esp.1': 'La Liga',
  'eng.1': 'Premier League',
  'uefa.champions': 'Champions League',
  'uefa.europa': 'Europa League',
  'ita.1': 'Serie A',
  'ger.1': 'Bundesliga',
  'fra.1': 'Ligue 1',
  'nba': 'NBA',
  'nfl': 'NFL',
  'mlb': 'MLB',
  'nhl': 'NHL',
};

function detectLeagueName(slug: string, eventTitle: string, widgetConfig: any): string {
  if (widgetConfig?.metadata?.matchInfo?.league) {
    const leagueCode = widgetConfig.metadata.matchInfo.league;
    return LEAGUE_NAMES[leagueCode] || leagueCode;
  }

  const slugLower = slug.toLowerCase();
  if (slugLower.startsWith('lal-')) return 'La Liga';
  if (slugLower.startsWith('ucl-')) return 'Champions League';
  if (slugLower.startsWith('uel-')) return 'Europa League';
  if (slugLower.startsWith('epl-')) return 'Premier League';
  if (slugLower.startsWith('ser-')) return 'Serie A';
  if (slugLower.startsWith('bun-')) return 'Bundesliga';
  if (slugLower.startsWith('lig-')) return 'Ligue 1';
  if (slugLower.includes('nba')) return 'NBA';
  if (slugLower.includes('nfl')) return 'NFL';
  if (slugLower.includes('mlb')) return 'MLB';
  if (slugLower.includes('nhl')) return 'NHL';

  const titleLower = eventTitle.toLowerCase();
  if (titleLower.includes('la liga') || titleLower.includes('laliga')) return 'La Liga';
  if (titleLower.includes('premier league')) return 'Premier League';
  if (titleLower.includes('champions league')) return 'Champions League';
  if (titleLower.includes('europa league')) return 'Europa League';
  if (titleLower.includes('serie a')) return 'Serie A';
  if (titleLower.includes('bundesliga')) return 'Bundesliga';
  if (titleLower.includes('ligue 1')) return 'Ligue 1';

  return 'Match';
}

function TeamCard({ market, teamLogos, getLivePrice, getLiveBuyPrices, openTradingModal }: {
  market: Market;
  teamLogos: Record<string, string>;
  getLivePrice: GetLivePriceFn;
  getLiveBuyPrices: GetLiveBuyPricesFn;
  openTradingModal: OpenTradingModalFn;
}) {
  const [yesPrice] = getLivePrice(market);
  const { buyYes } = getLiveBuyPrices(market);
  const percent = ((yesPrice ?? 0) * 100).toFixed(0);
  const teamName = getTeamName(market);
  const marketActive = market.active && market.acceptingOrders && !market.closed;
  const [imgFailed, setImgFailed] = useState(false);
  // Prefer the team-specific logo from our sports-live map. Polymarket
  // ships league-wide art on every market.image (Serie-A-Logo.png), so
  // using that as the source means every match shows the same icon.
  // Fall back to market.image only if we can't find a team match.
  const teamLogo = lookupTeamLogo(teamLogos, teamName);
  const imageSrc = teamLogo || market.image;
  const showImage = !!imageSrc && !imgFailed;

  return (
    <div className="flex-1 text-center">
      <div className="relative w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-white/10">
        {showImage ? (
          <CdnImage src={imageSrc!} alt={teamName} fill className="object-cover" onError={() => setImgFailed(true)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-xl md:text-2xl font-bold text-gray-400 dark:text-gray-500">{teamName.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
      <h3 className="font-satoshi font-bold text-base md:text-lg text-gray-900 dark:text-white mb-1">
        {teamName}
      </h3>
      <div className="text-2xl md:text-3xl font-inter-tight font-black text-gray-900 dark:text-white mb-3">
        {percent}%
      </div>
      <button
        onClick={() => {
          if (marketActive) {
            openTradingModal({
              isOpen: true,
              marketSlug: market.slug,
              outcome: "YES",
              outcomePrice: buyYes,
              marketQuestion: market.question,
              actualOutcome: teamName,
            });
          }
        }}
        disabled={!marketActive}
        className={`w-full max-w-[140px] py-2.5 rounded-[5px] font-inter-tight font-black text-sm transition-colors ${
          marketActive
            ? "bg-[#22C55E] text-white hover:brightness-110 active:scale-[0.98] shadow-[0_2px_12px_rgba(34,197,94,0.3)]"
            : "bg-gray-300 dark:bg-[#1F2937] text-gray-500 cursor-not-allowed opacity-50"
        }`}
      >
        {teamName.split(' ').pop()} {((buyYes ?? 0) * 100).toFixed(1)}¢
      </button>
    </div>
  );
}

// Live-ticking relative timestamp ("3s ago", "12m ago"). Re-renders
// every 5s so the footer stays accurate without polling the API.
function RelativeTime({ iso }: { iso: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return <>{diffSec}s ago</>;
  if (diffSec < 3600) return <>{Math.floor(diffSec / 60)}m ago</>;
  if (diffSec < 86400) return <>{Math.floor(diffSec / 3600)}h ago</>;
  return <>{Math.floor(diffSec / 86400)}d ago</>;
}

export default function SportsMatchView({
  markets, eventData, source, slug, widgetConfig,
  getLivePrice, getLiveBuyPrices, openTradingModal, formatVolume,
}: SportsMatchViewProps) {
  const t = useT();
  // Kalshi: live status + per-event score snapshot via useKalshiLive* hooks.
  // Polymarket: live flag + "2-0" score + period come directly from Gamma on eventData.
  const eventTicker = ((eventData as any)?.eventTicker || (eventData as any)?.id || slug || '').toUpperCase();
  const { data: liveSet } = useKalshiLiveTickers(source === 'kalshi');
  const { data: liveState } = useKalshiLiveState(source === 'kalshi');
  const isKalshiLive = source === 'kalshi' && !!liveSet?.has(eventTicker);
  const isPolymarketLive = source === 'polymarket' && (eventData as any)?.live === true;
  const pmEnded = source === 'polymarket' && (eventData as any)?.ended === true;
  // FINAL badge: match has wrapped (per the sports WS). Replaces the
  // LIVE pulse so users don't think a finished game is still in play.
  const isLive = (isKalshiLive || isPolymarketLive) && !pmEnded;
  const kalshiSnap = source === 'kalshi' && liveState ? liveState[eventTicker] : undefined;
  const pmTurn = source === 'polymarket' ? ((eventData as any)?.turn as string | null) : null;
  const pmLastUpdate = source === 'polymarket' ? ((eventData as any)?.lastUpdate as string | null) : null;
  const pmFinishedAt = source === 'polymarket' ? ((eventData as any)?.finishedTimestamp as string | null) : null;

  // Parse Polymarket's "home-away" score string into homeScore/awayScore.
  const pmScoreRaw = (isPolymarketLive || pmEnded) ? ((eventData as any)?.score as string | null) : null;
  const pmPeriod = (isPolymarketLive || pmEnded) ? ((eventData as any)?.period as string | null) : null;
  const pmScoreParts = pmScoreRaw ? pmScoreRaw.split('-').map(s => parseInt(s.trim(), 10)) : null;
  const pmHome = pmScoreParts && !isNaN(pmScoreParts[0]) ? pmScoreParts[0] : null;
  const pmAway = pmScoreParts && !isNaN(pmScoreParts[1]) ? pmScoreParts[1] : null;

  // Unified snapshot shape the existing score renderer reads. Polymarket uses
  // homeScore/awayScore/periodLabel — the same branch the Kalshi fallback uses.
  const liveSnap = kalshiSnap || (isPolymarketLive && pmHome != null && pmAway != null
    ? ({ homeScore: pmHome, awayScore: pmAway, periodLabel: pmPeriod || t("sportsMatch.live") } as any)
    : undefined);

  const sortedMarkets = [...markets].sort((a, b) => {
    const [priceA] = getLivePrice(a);
    const [priceB] = getLivePrice(b);
    return priceB - priceA;
  });

  const drawMarket = sortedMarkets.find(m =>
    (m.question || '').toLowerCase().includes('draw')
  );
  const teamMarkets = sortedMarkets.filter(m =>
    !(m.question || '').toLowerCase().includes('draw')
  ).slice(0, 2);

  const leagueName = detectLeagueName(slug, eventData.title, widgetConfig);

  const { data: teamLogos = {} } = useTeamLogos();

  return (
    <div className="mb-4 md:mb-6">
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden">
        {/* Match Header */}
        <div className="p-4 md:p-6">
          <div className="text-center mb-4 flex items-center justify-center gap-2">
            <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {leagueName}
            </span>
            {isLive && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-[5px] h-1.5 w-1.5 bg-red-500" />
                </span>
                <span className="text-[10px] font-black text-red-500 tracking-wider">{t("sportsMatch.live")}</span>
              </span>
            )}
            {pmEnded && (
              <span className="flex items-center px-2 py-0.5 rounded-[5px] bg-gray-500/10 border border-gray-500/20">
                <span className="text-[10px] font-black text-gray-500 dark:text-gray-400 tracking-wider">FINAL</span>
              </span>
            )}
            {isLive && pmTurn && (
              <span className="flex items-center px-2 py-0.5 rounded-[5px] bg-emerald-500/10 border border-emerald-500/20">
                <span className="text-[10px] font-black text-emerald-500 tracking-wider">{pmTurn} POSSESSION</span>
              </span>
            )}
          </div>

          {/* Teams Row */}
          <div className="flex items-center justify-between gap-4">
            {teamMarkets[0] && (
              <TeamCard
                market={teamMarkets[0]}
                teamLogos={teamLogos}
                getLivePrice={getLivePrice}
                getLiveBuyPrices={getLiveBuyPrices}
                openTradingModal={openTradingModal}
              />
            )}

            {/* Live score (Kalshi) — falls back to "VS" when no live snapshot */}
            <div className="flex-shrink-0 px-2 text-center">
              {isLive && liveSnap ? (
                liveSnap.competitors && liveSnap.competitors.length === 2 ? (
                  <>
                    <div className="flex items-center justify-center gap-1.5 tabular-nums">
                      {(liveSnap.competitors[0]?.roundScores || []).map((s: number, i: number) => (
                        <span key={`a${i}`} className="text-base md:text-lg font-monument font-bold text-gray-700 dark:text-gray-200">{s}</span>
                      ))}
                      {liveSnap.competitors[0]?.currentRoundScore != null && (
                        <span className="text-base md:text-lg font-monument font-bold text-emerald-500">{String(liveSnap.competitors[0].currentRoundScore)}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 my-0.5">
                      {liveSnap.periodLabel || t("sportsMatch.live")}
                    </div>
                    <div className="flex items-center justify-center gap-1.5 tabular-nums">
                      {(liveSnap.competitors[1]?.roundScores || []).map((s: number, i: number) => (
                        <span key={`b${i}`} className="text-base md:text-lg font-monument font-bold text-gray-700 dark:text-gray-200">{s}</span>
                      ))}
                      {liveSnap.competitors[1]?.currentRoundScore != null && (
                        <span className="text-base md:text-lg font-monument font-bold text-emerald-500">{String(liveSnap.competitors[1].currentRoundScore)}</span>
                      )}
                    </div>
                  </>
                ) : liveSnap.homeScore != null || liveSnap.awayScore != null ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="text-3xl md:text-4xl font-monument font-bold tabular-nums text-gray-900 dark:text-white">
                      {liveSnap.homeScore ?? 0} - {liveSnap.awayScore ?? 0}
                    </div>
                    {liveSnap.periodLabel && (
                      <div className="text-[10px] text-red-500 font-bold uppercase tracking-wider">
                        {liveSnap.periodLabel}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-2xl md:text-3xl font-monument font-bold text-gray-300 dark:text-gray-600">{t("sportsMatch.vs")}</div>
                )
              ) : (
                <div className="text-2xl md:text-3xl font-monument font-bold text-gray-300 dark:text-gray-600">{t("sportsMatch.vs")}</div>
              )}
            </div>

            {teamMarkets[1] && (
              <TeamCard
                market={teamMarkets[1]}
                teamLogos={teamLogos}
                getLivePrice={getLivePrice}
                getLiveBuyPrices={getLiveBuyPrices}
                openTradingModal={openTradingModal}
              />
            )}
          </div>

          {/* Draw Option */}
          {drawMarket && (() => {
            const [yesPrice] = getLivePrice(drawMarket);
            const { buyYes } = getLiveBuyPrices(drawMarket);
            const percent = ((yesPrice ?? 0) * 100).toFixed(0);
            const marketActive = drawMarket.active && drawMarket.acceptingOrders && !drawMarket.closed;

            return (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
                <div className="flex items-center justify-center gap-4">
                  <span className="text-sm font-satoshi text-gray-600 dark:text-gray-400">{t("sportsMatch.draw")}</span>
                  <span className="text-lg font-inter-tight font-black text-gray-900 dark:text-white">
                    {percent}%
                  </span>
                  <button
                    onClick={() => {
                      if (marketActive) {
                        openTradingModal({
                          isOpen: true,
                          marketSlug: drawMarket.slug,
                          outcome: "YES",
                          outcomePrice: buyYes,
                          marketQuestion: drawMarket.question,
                          actualOutcome: "Draw",
                        });
                      }
                    }}
                    disabled={!marketActive}
                    className={`px-6 py-2 rounded-[5px] font-inter-tight font-bold text-sm transition-all ${
                      marketActive
                        ? "bg-gray-200 dark:bg-[#1A1A1A] text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-[#222] border border-gray-300 dark:border-[#333]"
                        : "bg-gray-100 dark:bg-[#111] text-gray-400 cursor-not-allowed opacity-50"
                    }`}
                  >
                    {t("sportsMatch.draw")} {((buyYes ?? 0) * 100).toFixed(1)}¢
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Match Stats */}
        <div className="px-4 md:px-6 py-3 bg-gray-50 dark:bg-[#0f0f12] border-t border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-center justify-center gap-6 text-xs font-inter-tight text-gray-500 dark:text-gray-400 flex-wrap">
            <span>{t("sportsMatch.statsVol")} {formatVolume(eventData.volume)}</span>
            <span>•</span>
            <span>{source === 'kalshi' && eventData.openInterest ? t("sportsMatch.statsOi") : t("sportsMatch.statsLiq")} {formatVolume(source === 'kalshi' && eventData.openInterest ? eventData.openInterest : eventData.liquidity)}</span>
            {/* Live/ended timestamps from sports WS — confidence signal that the
                feed is alive (LIVE) and an authoritative "match wrapped at X"
                timestamp (FINAL). */}
            {pmEnded && pmFinishedAt && (
              <>
                <span>•</span>
                <span title={new Date(pmFinishedAt).toLocaleString()}>Finished <RelativeTime iso={pmFinishedAt} /></span>
              </>
            )}
            {isLive && pmLastUpdate && (
              <>
                <span>•</span>
                <span title={new Date(pmLastUpdate).toLocaleString()}>Updated <RelativeTime iso={pmLastUpdate} /></span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
